import { useState } from "react";
import { ActionIcon, Badge, Button, Card, Group, Select, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconTag, IconX } from "@tabler/icons-react";
import type { TagSubject } from "@safiri/shared";
import { isActiveTag, tagsApi } from "../../api/tags";
import { usePermission } from "../../auth/usePermission";
import { errorMessage } from "../../lib/axios";
import { formatDate, formatDateTime } from "../../lib/format";
import { useTagCatalogue } from "./TagChips";

/**
 * Identification tags on one driver or rider account. Tags are admin-only;
 * the apps never see them. Every add/remove is in the audit log.
 */
export function AccountTagsCard({ subject, accountId }: { subject: TagSubject; accountId: string }) {
  const qc = useQueryClient();
  const canEdit = usePermission(subject === "driver" ? "drivers.edit" : "riders.status");
  const { data: catalogue } = useTagCatalogue();
  const key = ["account-tags", subject, accountId];
  const tags = useQuery({ queryKey: key, queryFn: () => tagsApi.forAccount(subject, accountId) });

  const [picked, setPicked] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);

  const refresh = (next: unknown) => {
    qc.setQueryData(key, next);
    void qc.invalidateQueries({ queryKey: [subject === "driver" ? "drivers" : "users"] });
    void qc.invalidateQueries({ queryKey: ["tags-with-counts"] });
  };
  const assign = useMutation({
    mutationFn: () =>
      tagsApi.assign(subject, accountId, {
        key: picked!,
        note: note.trim() || null,
        expiresAt: expiresAt ? expiresAt.toISOString() : null,
      }),
    onSuccess: (next) => {
      refresh(next);
      setPicked(null);
      setNote("");
      setExpiresAt(null);
      notifications.show({ color: "green", message: "Tag added" });
    },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });
  const remove = useMutation({
    mutationFn: (tagKey: string) => tagsApi.remove(subject, accountId, tagKey),
    onSuccess: (next) => {
      refresh(next);
      notifications.show({ color: "green", message: "Tag removed" });
    },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const current = tags.data ?? [];
  const label = (k: string) => catalogue?.find((c) => c.key === k)?.label ?? k;
  const color = (k: string) => catalogue?.find((c) => c.key === k)?.color ?? "gray";
  const options = (catalogue ?? [])
    .filter((c) => !c.archived && c.appliesTo.includes(subject) && !current.some((t) => t.key === c.key))
    .map((c) => ({ value: c.key, label: c.label }));

  return (
    <Card withBorder radius="md" padding="md">
      <Group gap={6} mb="xs">
        <IconTag size={16} />
        <Text fw={600} size="sm">
          Tags
        </Text>
        <Text size="xs" c="dimmed">
          Only visible to admins
        </Text>
      </Group>
      <Stack gap="xs">
        {current.length === 0 && !tags.isLoading && (
          <Text size="sm" c="dimmed">
            No tags
          </Text>
        )}
        {current.map((t) => {
          const expired = !isActiveTag(t);
          return (
            <Group key={t.key} justify="space-between" wrap="nowrap">
              <Stack gap={0}>
                <Group gap={6}>
                  <Badge variant="light" color={expired ? "gray" : color(t.key)}>
                    {label(t.key)}
                  </Badge>
                  {expired && (
                    <Text size="xs" c="dimmed">
                      expired
                    </Text>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  Added {formatDateTime(t.assignedAt)} by {t.assignedByName ?? "—"}
                  {t.expiresAt ? ` · ${expired ? "expired" : "until"} ${formatDate(t.expiresAt)}` : ""}
                  {t.note ? ` · ${t.note}` : ""}
                </Text>
              </Stack>
              {canEdit && (
                <Tooltip label="Remove tag" withArrow>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label="Remove tag"
                    loading={remove.isPending && remove.variables === t.key}
                    onClick={() => remove.mutate(t.key)}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          );
        })}
        {canEdit && options.length > 0 && (
          <Group align="flex-end" gap="xs" wrap="wrap">
            <Select size="xs" w={180} label="Add tag" placeholder="Pick a tag" data={options} value={picked} onChange={setPicked} searchable />
            <TextInput size="xs" w={200} label="Note (optional)" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
            <DateInput size="xs" w={150} label="Expires (optional)" value={expiresAt} onChange={(d) => setExpiresAt(d ? new Date(d) : null)} minDate={new Date()} clearable />
            <Button size="xs" disabled={!picked} loading={assign.isPending} onClick={() => assign.mutate()}>
              Add
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
